import { useCallback, useEffect, useMemo } from 'react';
import { mutate } from 'swr';
import useSWRInfinite, { type SWRInfiniteConfiguration } from 'swr/infinite';
import { EngineService } from '@/services/engine';
import {
    getSourceFoldersKey,
    isSourceFoldersCacheKey,
    isSourceFoldersPageCacheKey,
} from '@/constants/cache-keys';
import { SOURCE_FOLDERS_PAGE_SIZE } from '@/constants/source-folders';
import { FolderOwnership } from '@/types/source-folder';

const defaultSwrConfig: SWRInfiniteConfiguration = {
    revalidateFirstPage: false,
};

export interface SourceFoldersQuery {
    ownership?: FolderOwnership;
    orderBy?: string;
    search?: string;
}

type FolderStreamRevalidator = () => Promise<unknown>;

const mountedFolderStreamRevalidators = new Set<FolderStreamRevalidator>();

// SWR's key-filter `mutate` deliberately skips `$inf$` keys, so a filter alone
// can never refresh a paginated folder stream. Mounted streams therefore
// register their own revalidator here.
export async function revalidateSourceFolders(): Promise<void> {
    // Only the stream for the active sort option is mounted; every other one
    // holds pages fetched before this change with no revalidator to refresh
    // them. Dropping those pages makes the next mount refetch rather than
    // serve a folder list from before the change.
    await mutate(isSourceFoldersPageCacheKey, undefined, { revalidate: false });
    await Promise.all([
        ...Array.from(mountedFolderStreamRevalidators, (revalidate) =>
            revalidate(),
        ),
        mutate(isSourceFoldersCacheKey),
    ]);
}

export function useSourceFoldersInfinite(
    excludeEmptyFolders: boolean,
    swrConfig?: SWRInfiniteConfiguration,
    query: SourceFoldersQuery = {},
) {
    const { ownership, orderBy, search } = query;

    const keyFn = useMemo(
        () =>
            getSourceFoldersKey(
                excludeEmptyFolders,
                ownership,
                orderBy,
                search,
            ),
        [excludeEmptyFolders, ownership, orderBy, search],
    );

    const swr = useSWRInfinite(
        keyFn,
        ([, , , , , pageIndex]: readonly [
            string,
            boolean,
            string,
            string,
            string,
            number,
        ]) =>
            EngineService.getSourceFolders({
                page: pageIndex + 1,
                size: SOURCE_FOLDERS_PAGE_SIZE,
                excludeEmpty: excludeEmptyFolders,
                ownership,
                orderBy,
                search,
            }),
        { ...defaultSwrConfig, ...swrConfig },
    );

    useEffect(() => {
        mountedFolderStreamRevalidators.add(swr.mutate);
        return () => {
            mountedFolderStreamRevalidators.delete(swr.mutate);
        };
    }, [swr.mutate]);

    const lastPage = swr.data?.at(-1)?.page;
    const hasMore = !!lastPage && lastPage.page < lastPage.totalPages;

    const loadMore = useCallback(() => {
        if (!swr.isValidating && hasMore) {
            void swr.setSize(swr.size + 1);
        }
    }, [hasMore, swr.isValidating, swr.setSize, swr.size]);

    return {
        data: swr.data,
        error: swr.error,
        isLoading: swr.isLoading,
        isValidating: swr.isValidating,
        mutate: swr.mutate,
        size: swr.size,
        setSize: swr.setSize,
        hasMore,
        loadMore,
    };
}
